/**
 * Writing's documents' numbers: paper sizes and margin presets (millimetres), how millimetres
 * become CSS pixels, the gap between sheets, the text sizes offered, how deep lists indent, and
 * a picture's width range and farthest offset.
 */
import type { PaperSize } from "@protocol/PaperSize";

/** The usual margins: 2.5 cm on every side (top, right, bottom, left, millimetres). */
export const NORMAL_MARGINS_MM: readonly [number, number, number, number] = [25, 25, 25, 25];

/** Each paper's width and height in millimetres, upright. */
export const PAPER: Record<PaperSize, [number, number]> = { a4: [210, 297], a5: [148, 210], letter: [216, 279], legal: [216, 356] };

/** Word's margin presets, in millimetres (top, right, bottom, left). */
export const MARGINS: { id: string; label: string; margins: [number, number, number, number] }[] = [
  { id: "normal", label: "Normal (2.5 cm)", margins: [...NORMAL_MARGINS_MM] },
  { id: "narrow", label: "Narrow (1.27 cm)", margins: [13, 13, 13, 13] },
  { id: "moderate", label: "Moderate", margins: [25, 19, 25, 19] },
  { id: "wide", label: "Wide (5 cm sides)", margins: [25, 51, 25, 51] },
];

/** Screen pixels in a millimetre (CSS: 96 to the inch). */
export const PX_PER_MM = 96 / 25.4;

/** The space between two sheets on the desk, in pixels. */
export const SHEET_GAP = 28;

/** The sizes offered, in points. */
export const DOC_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 36, 48, 72];

/** How far a paragraph may be indented, in steps. */
export const MAX_INDENT = 8;

/** The smallest and biggest width, in percent of the text's width. */
export const WIDTH_RANGE = [10, 100] as const;

/** How far a free picture may go from its anchor, in pixels of the paper. */
export const PICTURE_MAX_OFFSET = 2000;

/** The widest a margin may be (millimetres). */
export const MAX_MARGIN_MM = 80;

/** The least room left for the words on a sheet, whatever the margins (CSS pixels). */
export const MIN_CONTENT_PX = 80;

/** How far measured places may be off by rounding and still count as equal (CSS pixels). */
export const MEASURE_SLACK_PX = 0.5;

/** How many times per block a sheet may start earlier for a picture drawn above its block, before giving up. */
export const RISE_TRIES_PER_BLOCK = 4;
